/**
 * FeedbackButtons — 👍/👎 sur une réponse IA + signalement structuré de qualité.
 *
 * Collecte des données d'entraînement (DPO / fine-tuning) à chaque interaction.
 * Adopté de LES-LEX PL (2026-06-02).
 *
 * Props:
 *   source        — "advice" | "studio_chat" | "creator" | "contract_analysis" | "maitre"
 *   query         — la question/input de l'utilisateur
 *   aiResponse    — la réponse IA (texte)
 *   ragContext    — (optionnel) contexte RAG utilisé
 *   metadata      — (optionnel) { model, language, project_id, doc_type }
 *   className     — classes CSS additionnelles
 */
import { useState } from "react";
import { ThumbsUp, ThumbsDown, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { submitFeedbackRate } from "@/lib/api";

// Types de problèmes pour 👎 (multi-select) — francisé
const QUALITY_ISSUES = [
  { id: "factual_error", label: "Erreur factuelle" },
  { id: "terminology", label: "Terminologie incorrecte" },
  { id: "citation", label: "Citation erronée / manquante" },
  { id: "incomplete", label: "Réponse incomplète" },
  { id: "wrong_basis", label: "Mauvaise base légale" },
  { id: "hallucination", label: "Information inventée" },
  { id: "other", label: "Autre" },
];

export default function FeedbackButtons({
  source,
  query = "",
  aiResponse = "",
  ragContext = "",
  metadata = {},
  className = "",
}) {
  const [submitted, setSubmitted] = useState(null); // null | "up" | "down"
  const [sending, setSending] = useState(false);
  const [downOpen, setDownOpen] = useState(false);
  const [issues, setIssues] = useState([]);
  const [details, setDetails] = useState("");

  const send = async (action, extra = {}) => {
    setSending(true);
    try {
      await submitFeedbackRate({
        source,
        user_action: action,
        query: (query || "").slice(0, 5000),
        ai_response: (aiResponse || "").slice(0, 10000),
        rag_context: (ragContext || "").slice(0, 3000),
        metadata: { language: "fr", ...metadata },
        ...extra,
      });
      setSubmitted(action === "thumbs_up" ? "up" : "down");
      toast.success(action === "thumbs_up" ? "Merci pour votre retour !" : "Merci, nous allons améliorer cela.");
    } catch (e) {
      toast.error("Erreur lors de l'envoi du retour");
    } finally {
      setSending(false);
      setDownOpen(false);
    }
  };

  const toggleIssue = (id) => {
    setIssues((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const submitDown = () => {
    send("thumbs_down", { quality_issues: issues, details_text: details.slice(0, 2000) });
  };

  if (submitted) {
    return (
      <div className={`flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}>
        <Check className="h-3.5 w-3.5 text-green-600" />
        <span>Retour enregistré — merci !</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <span className="text-xs text-muted-foreground mr-1">Cette réponse vous a-t-elle aidé ?</span>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 hover:text-green-600"
        onClick={() => send("thumbs_up")}
        disabled={sending}
        title="Réponse utile"
      >
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
      </Button>

      <Popover open={downOpen} onOpenChange={setDownOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 hover:text-red-600"
            disabled={sending}
            title="Réponse à améliorer"
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-3">
            <p className="text-sm font-medium">Qu'est-ce qui n'allait pas ?</p>
            <div className="flex flex-wrap gap-1.5">
              {QUALITY_ISSUES.map((iss) => (
                <Badge
                  key={iss.id}
                  variant={issues.includes(iss.id) ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => toggleIssue(iss.id)}
                >
                  {iss.label}
                </Badge>
              ))}
            </div>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Détails (optionnel)..."
              rows={2}
              maxLength={2000}
              className="text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDownOpen(false)}>Annuler</Button>
              <Button size="sm" onClick={submitDown} disabled={sending}>
                {sending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                Envoyer
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
